import { useRef } from 'react';
import type React from 'react';

import { Alert, Avatar, Button, Link, Text } from '@change/ds-core-ui';
import { CheckIcon, DecisionMakerIcon, GovBuildingIcon } from '@change/ds-icons';

import { Translate, useI18n } from '@change-corgi/core-react/i18n';

import { isError, isLoaded, isLoading } from '#src/app/shared/utils/async';

export type RecommendedDecisionMaker = {
	normalizedId: string;
	canonicalPetitionTargetId: string | null;
	name: string;
	type: 'PERSON' | 'ORG';
	title: string | null;
	photoUrl: string | null;
};
type Status = 'idle' | 'loading' | 'loaded' | 'error';
type Data = RecommendedDecisionMaker;
type Error = { type: 'ADD' | 'REMOVE' };
type RecommendedDecisionMakerState =
	| { data: Data; status: Exclude<Status, 'error'> }
	| { data: Data; status: Extract<Status, 'error'>; error: Error };

type RecommendedDecisionMakerCardProps = {
	state: RecommendedDecisionMakerState;
	onAdd: (selectedDecisionMaker: RecommendedDecisionMaker) => Promise<void>;
	onRemove: (selectedDecisionMaker: RecommendedDecisionMaker) => Promise<void>;
};

export const RecommendedDecisionMakerCard: React.FC<RecommendedDecisionMakerCardProps> = ({
	state,
	onAdd,
	onRemove,
}) => {
	const recommendedDecisionMaker = state.data;
	const { translate } = useI18n();
	const addButtonRef = useRef<HTMLButtonElement>(null);
	const removeLinkRef = useRef<HTMLButtonElement>(null);

	const DefaultIcon = recommendedDecisionMaker.type === 'ORG' ? GovBuildingIcon : DecisionMakerIcon;

	return (
		<div data-qa="dm-recommender-card" className="rounded-lg border p-4">
			{isError(state) ? (
				<Alert className="mb-4" variant="error">
					<Translate value={`corgi.components.dm_recommender.dm_card.errors.${state.error.type.toLowerCase()}`} />
				</Alert>
			) : null}
			<div className="inline-flex items-start gap-4">
				{recommendedDecisionMaker.photoUrl ? (
					<Avatar alt="avatar" size="small" src={recommendedDecisionMaker.photoUrl} />
				) : (
					<div className="bg-fill-weaker flex size-12 items-center justify-center rounded-full">
						<DefaultIcon className="text-icon-weak" size={24} />
					</div>
				)}
				<div className="flex flex-col items-start leading-none">
					<Text fontWeight="bold">{recommendedDecisionMaker.name}</Text>
					{recommendedDecisionMaker.title && (
						<Text size="small" fontWeight="normal">
							{recommendedDecisionMaker.title}
						</Text>
					)}

					<div aria-live="polite">
						{isLoaded(state) ? (
							<div className="mt-4 flex items-center gap-4">
								<div className="flex items-center gap-2">
									<CheckIcon className="text-icon-success" size={24} />
									<Text size="small" fontWeight="bold" className="text-typography-success">
										<Translate value="corgi.components.dm_recommender.dm_card.success" />
									</Text>
								</div>
								<Link
									as="button"
									ref={removeLinkRef}
									size="small"
									variant="secondary"
									onClick={async () => {
										await onRemove(recommendedDecisionMaker);
										addButtonRef.current?.focus();
									}}
								>
									<Translate value="corgi.components.dm_recommender.dm_card.remove_link" />
								</Link>
							</div>
						) : (
							<Button
								ref={addButtonRef}
								className="mt-4 min-w-[87px] px-4"
								variant="primary"
								size="small"
								onClick={async () => {
									await onAdd(recommendedDecisionMaker);
									removeLinkRef.current?.focus();
								}}
								loading={isLoading(state)}
								aria-label={translate('corgi.components.dm_recommender.dm_card.cta')}
							>
								<Translate value="corgi.components.dm_recommender.dm_card.cta" />
							</Button>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};
